// Sessions (SPEC F10 base, §8 Session): the journal's rows. The `sessions` table is Dexie v4 (DECISIONS 2026-10-08).
// A row is written when a session starts and extended while you work (`endedAt` always holds the last activity).
// The check-out fills in the outcome afterwards; "done" also sets `Project.finishedAt`, in the same transaction.
// Sessions belong to their project: Step D's export carries them, a purge of the project deletes them.

import type { Session, SessionEnergy, SessionOutcome } from '../../data/types';
import type { JournalWrite } from '../../lib/journal';
import { getDb } from '../schema';

/** Applies the journal's writes, in one transaction (a start and an extend never half-apply). */
export async function applyJournalWrites(writes: readonly JournalWrite[]): Promise<void> {
  if (writes.length === 0) return;
  const db = getDb();
  await db.transaction('rw', db.sessions, async () => {
    for (const w of writes) {
      if (w.op === 'start') {
        await db.sessions.put(w.session);
        continue;
      }
      const s = await db.sessions.get(w.id);
      if (!s) continue;
      await db.sessions.put({
        ...s,
        endedAt: w.endedAt,
        stepIds: w.stepId && !s.stepIds.includes(w.stepId) ? [...s.stepIds, w.stepId] : s.stepIds,
        phaseIds: w.phaseId && !s.phaseIds.includes(w.phaseId) ? [...s.phaseIds, w.phaseId] : s.phaseIds,
      });
    }
  });
}

export async function getSession(id: string): Promise<Session | undefined> {
  return getDb().sessions.get(id);
}

/** A project's sessions, oldest first (Step D's export carries these). */
export async function listSessions(projectId: string): Promise<Session[]> {
  return getDb().sessions.where('projectId').equals(projectId).sortBy('startedAt');
}

/** The few newest sessions across projects, newest first: enough to find the one to offer a check-out for. */
export async function recentSessions(limit = 5): Promise<Session[]> {
  return getDb().sessions.orderBy('startedAt').reverse().limit(limit).toArray();
}

export interface CheckOutInput {
  outcome: SessionOutcome;
  energy?: SessionEnergy;
  /** A stuck check-out names the phase, and may leave a note. */
  stuckPhaseId?: string;
  stuckNote?: string;
}

export interface CheckOutResult {
  /** For "done": the project's finishedAt before, so Undo can put it back. `null` when there was none. */
  prevFinishedAt?: number | null;
}

/**
 * Records the check-out. "Done" means bounced on the MPC: it sets the project's `finishedAt` to the time the
 * session ended (a later "done" overwrites it), in the same transaction. Only the fields given are written.
 */
export async function checkOut(id: string, input: CheckOutInput, now: number): Promise<CheckOutResult> {
  const db = getDb();
  return db.transaction('rw', db.sessions, db.projects, async () => {
    const s = await db.sessions.get(id);
    if (!s) return {};
    const row: Session = { ...s, outcome: input.outcome };
    if (input.energy) row.energy = input.energy;
    if (input.outcome === 'stuck') {
      if (input.stuckPhaseId) row.stuckPhaseId = input.stuckPhaseId;
      const note = input.stuckNote?.trim();
      if (note) row.stuckNote = note;
    }
    await db.sessions.put(row);
    if (input.outcome !== 'done') return {};
    const project = await db.projects.get(s.projectId);
    if (!project) return {};
    await db.projects.put({ ...project, finishedAt: s.endedAt ?? now });
    return { prevFinishedAt: project.finishedAt ?? null };
  });
}

/** Undo of "done": the session is unanswered again and the project's finishedAt is what it was. */
export async function undoDone(id: string, prevFinishedAt: number | null | undefined): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.sessions, db.projects, async () => {
    const s = await db.sessions.get(id);
    if (!s) return;
    const unanswered: Session = { ...s };
    delete unanswered.outcome;
    delete unanswered.energy;
    delete unanswered.stuckPhaseId;
    delete unanswered.stuckNote;
    await db.sessions.put(unanswered);
    const project = await db.projects.get(s.projectId);
    if (!project) return;
    const next = { ...project };
    if (prevFinishedAt === null || prevFinishedAt === undefined) delete next.finishedAt;
    else next.finishedAt = prevFinishedAt;
    await db.projects.put(next);
  });
}
